import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMeters,
  getMeter,
  createMeter,
  updateMeter,
  deleteMeter,
  getKPIs,
  getKPI,
  createKPI,
  deleteKPI,
  getGraphData,
  getMeterAlertInstances,
  getMeterReadingCounts,
  getAllAlertInstances
} from '../services/meterApi'; // Adjust path as needed

/* ==================== Meters Hooks ==================== */

/**
 * Hook to fetch all meters
 */
export const useMeters = () => {
  return useQuery('meters', getMeters, {
    staleTime: 1000, // 5 minutes
    cacheTime: 1000,
  });
};

/**
 * Hook to fetch single meter
 */
export const useMeter = (meterId) => {
  return useQuery(
    ['meter', meterId],
    () => getMeter(meterId),
    {
      enabled: !!meterId,
      staleTime: 1000,
      cacheTime: 1000,
    }
  );
};

/**
 * Hook to create meter
 */
export const useCreateMeter = () => {
  const queryClient = useQueryClient();
  
  return useMutation(createMeter, {
    onSuccess: () => {
      queryClient.invalidateQueries('meters');
    },
  });
};

/**
 * Hook to update meter
 */
export const useUpdateMeter = (meterId) => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (data) => updateMeter(meterId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('meters');
        queryClient.invalidateQueries(['meter', meterId]);
      },
    }
  );
};

/**
 * Hook to delete meter
 */
export const useDeleteMeter = () => {
  const queryClient = useQueryClient();
  
  return useMutation(deleteMeter, {
    onSuccess: () => {
      queryClient.invalidateQueries('meters');
    },
  });
};

/* ==================== KPI Hooks ==================== */

/**
 * Hook to fetch all KPIs
 * @param {object} params - { kpiType: 'INTERNAL' | 'EXTERNAL' }
 */
export const useKPIs = (params = {}) => {
  return useQuery(
    ['kpis', params],
    () => getKPIs(params),
    {
      staleTime: 1000,
      cacheTime: 1000,
    }
  );
};

/**
 * Hook to fetch single KPI
 */
export const useKPI = (kpiId) => {
  return useQuery(
    ['kpi', kpiId],
    () => getKPI(kpiId),
    {
      enabled: !!kpiId,
      staleTime: 1000,
    }
  );
};

/**
 * Hook to create KPI
 */
export const useCreateKPI = () => {
  const queryClient = useQueryClient();
  
  return useMutation(createKPI, {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
    },
  });
};

/**
 * Hook to delete KPI
 */
export const useDeleteKPI = () => {
  const queryClient = useQueryClient();
  
  return useMutation(deleteKPI, {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
    },
  });
};

/* ==================== Graph Data Hooks ==================== */

/**
 * Hook to fetch graph data
 * Use this for all meter visualizations
 */
export const useGraphData = (graphConfig, options = {}) => {
  return useQuery(
    ['graphData', graphConfig],
    () => getGraphData(graphConfig),
    {
      enabled: !!graphConfig && options.enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
      ...options
    }
  );
};

/**
 * Hook for manual graph data fetching
 * Useful when you need to trigger graph fetch manually
 */
export const useGraphDataMutation = () => {
  return useMutation(getGraphData);
};
/* ==================== Meter Trend Hooks ==================== */

/**
 * Fetches fourteen days of daily consumption for every meter in one request.
 * Grouping by meter server-side keeps a grid of cards to a single call.
 * @param {object} trendConfig - Widget config from buildTrendConfig.
 * @returns {object} react-query result whose data holds the raw rows.
 */
export const useMeterTrends = (trendConfig) => {
  return useQuery(
    ['meterTrends', trendConfig],
    () => getGraphData(trendConfig),
    {
      enabled: !!trendConfig,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
      retry: false,
    }
  );
};

/**
 * Alert history for one meter, used by the downtime timeline.
 * @param {string} meterId - The meter to fetch history for.
 * @returns {object} React Query result holding the alert instances.
 */
/**
 * Alert history across every meter, for the downtime report.
 *
 * Held longer than the per-meter query: the report is opened, read and closed,
 * and re-opening it inside the window should not re-fetch a table that the
 * status job appends to a few times a month.
 *
 * @returns {object} React-query result carrying every alert instance.
 */
export const useFleetAlerts = (enabled = true) =>
  useQuery(['fleet-alerts'], getAllAlertInstances, {
    enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: false,
  });

export const useMeterAlerts = (meterId) =>
  useQuery(
    ['meter-alerts', meterId],
    () => getMeterAlertInstances(meterId),
    {
      enabled: !!meterId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: false,
    }
  );

/**
 * Alert history for every meter, in one request.
 *
 * The meters list dates each card from when its meter last returned to normal,
 * which lives in the alert history. Fetching that per card would be one
 * request per meter in the grid for a table small enough to return whole.
 *
 * @returns {object} React Query result holding every alert instance.
 */
export const useAllMeterAlerts = () =>
  useQuery(
    ['meter-alerts', 'all'],
    () => getMeterAlertInstances(),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: false,
    }
  );

/**
 * Per-day reading counts for one meter, used by the data completeness strip.
 * @param {string} meterId - The meter to report on.
 * @param {number} days - How many days back to look.
 * @returns {object} React Query result holding the counts.
 */
export const useMeterReadingCounts = (meterId, days = 30) =>
  useQuery(
    ['meter-reading-counts', meterId, days],
    () => getMeterReadingCounts(meterId, days),
    {
      enabled: !!meterId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: false,
    }
  );
